import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnnotationLinkId, AnnotationLinkStyle, ViewModelId } from 'balus-common';
import { Key } from '@framework/domain';
import { useExclusiveLinkEditing } from '@model-framework/link';
import { useOtherUserEditingToast } from '@view-model/models/framework/text/editing-user/useOtherUserEditingToast';
import type { ModelElementId } from '@view-model/domain/key';
import { RightClickMenu, RightClickMenuItem } from '@framework/ui/elements/RightClickMenu';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import type { AnnotationLink } from '../domain/AnnotationLink';
import type { DragContext } from '@model-framework/ui';
import { Point } from '@view-model/models/common/basic';
import type { LinkPlacement } from '@view-model/models/sticky/StickyLink/domain/LinkPlacement';
import { LinkBend } from '@view-model/models/sticky/StickyLink/domain/vo/LinkBend';
import { BendingPoint } from '@view-model/models/sticky/StickyLink/components/LinkLine/BendingPoint';
import { AnnotationLinkShape } from './AnnotationLinkShape';
import { AnnotationLinkMenu } from './AnnotationLinkMenu';
import { AnnotationLinkStyleMenuButton } from './AnnotationLinkStyleMenuButton';

const LineColor = '#A0AEC0';
const SelectedLineColor = '#64748B';
const LineThickness = 4;
const HitAreaWidth = 16;

type Props = {
  viewModelId: ViewModelId;
  link: AnnotationLink;
  placement: LinkPlacement;
  isSelected: boolean;
  showMenu: boolean;
  readonly: boolean;
  passThrough: boolean;
  onClick(id: ModelElementId, e: React.MouseEvent<SVGGElement>): void;
  onStyleSelected(id: AnnotationLinkId, style: AnnotationLinkStyle): void;
  onReverse(id: AnnotationLinkId): void;
  onBendChanged(id: AnnotationLinkId, bend: LinkBend): void;
  onDelete(): void;
};

export const AnnotationLinkView: React.FC<Props> = ({
  viewModelId,
  link,
  placement,
  isSelected,
  showMenu,
  readonly,
  passThrough,
  onClick,
  onStyleSelected,
  onReverse,
  onBendChanged,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const { activeRightClickMenuIds, setActiveRightClickMenuIds } = useRightClickMenuOpenState();
  const { id } = link;
  const [draggingBendingPoint, setDraggingBendingPoint] = useState<Point | null>(null);
  const draggingBendingPointRef = useRef<Point | null>(null);
  const bendingPointMovedRef = useRef(false);
  const editingKey = useMemo(() => new Key([['AnnotationLink', id]]), [id]);
  const { otherUserEditing, startEdit, endEdit } = useExclusiveLinkEditing(editingKey, viewModelId);
  const notifyOtherUserEditingToast = useOtherUserEditingToast();
  const bendingPoint = draggingBendingPoint ?? Point.fromPosition(placement.bendingPoint(link.bend));
  const bezier = placement.bezierLine(bendingPoint);

  const handleBendingPointDragStart = useCallback(() => {
    if (otherUserEditing) {
      notifyOtherUserEditingToast(id);
      return;
    }
    bendingPointMovedRef.current = false;
    draggingBendingPointRef.current = bendingPoint;
    setDraggingBendingPoint(bendingPoint);
  }, [otherUserEditing, notifyOtherUserEditingToast, id, bendingPoint]);
  const handleBendingPointDrag = useCallback(
    ({ dx, dy }: DragContext) => {
      if (otherUserEditing) {
        notifyOtherUserEditingToast(id);
        return;
      }
      // クリックだけでも開始と終了が呼ばれる。押して離すまでが通信の往復より短いと編集中の登録と解除の順序が入れ替わり、
      // 登録が残って他のユーザーが曲げられなくなるため、実際に動かし始めてから登録する
      if ((dx !== 0 || dy !== 0) && !bendingPointMovedRef.current) {
        bendingPointMovedRef.current = true;
        startEdit();
      }
      draggingBendingPointRef.current = draggingBendingPointRef.current?.addXY(dx, dy) ?? null;
      setDraggingBendingPoint(draggingBendingPointRef.current);
    },
    [otherUserEditing, notifyOtherUserEditingToast, id, startEdit]
  );
  const handleBendingPointDragEnd = useCallback(() => {
    // 開始から終了までが再描画より早いと state は古い値のままなので、ドラッグ中の位置は ref から読む
    const draggedPoint = draggingBendingPointRef.current;
    if (!draggedPoint) return;

    // 動かしていないときに確定すると、逆算した曲げ具合の誤差で見た目の変わらない undo 履歴が積まれる
    if (!otherUserEditing && bendingPointMovedRef.current) {
      onBendChanged(id, placement.bend(draggedPoint));
      endEdit();
    }
    draggingBendingPointRef.current = null;
    setDraggingBendingPoint(null);
  }, [otherUserEditing, onBendChanged, id, placement, endEdit]);
  const handleBendingPointDblClick = useCallback(() => {
    if (otherUserEditing) {
      notifyOtherUserEditingToast(id);
      return;
    }
    onBendChanged(id, LinkBend.Straight());
  }, [otherUserEditing, notifyOtherUserEditingToast, onBendChanged, id]);

  const handleClick = useCallback((e: React.MouseEvent<SVGGElement>) => onClick(id, e), [onClick, id]);
  const handleRightClick = useCallback(
    (e: React.MouseEvent<SVGGElement>) => {
      onClick(id, e);
      setActiveRightClickMenuIds([id]);
      e.preventDefault();
    },
    [onClick, id, setActiveRightClickMenuIds]
  );
  const handleStyleSelected = useCallback(
    (style: AnnotationLinkStyle) => {
      setActiveRightClickMenuIds([]);
      onStyleSelected(id, style);
    },
    [onStyleSelected, id, setActiveRightClickMenuIds]
  );
  const handleReverse = useCallback(() => {
    setActiveRightClickMenuIds([]);
    onReverse(id);
  }, [onReverse, id, setActiveRightClickMenuIds]);
  const handleDelete = useCallback(() => {
    setActiveRightClickMenuIds([]);
    onDelete();
  }, [onDelete, setActiveRightClickMenuIds]);

  return (
    <>
      <g
        cursor="pointer"
        pointerEvents={passThrough ? 'none' : undefined}
        onClick={handleClick}
        onContextMenu={readonly ? undefined : handleRightClick}
      >
        <path
          d={bezier.getSVGPath()}
          fill="none"
          stroke="transparent"
          strokeWidth={HitAreaWidth}
          pointerEvents="stroke"
        />
        <AnnotationLinkShape
          style={link.style}
          bezier={bezier}
          reversed={link.reversed}
          color={isSelected ? SelectedLineColor : LineColor}
          thickness={LineThickness}
        />
      </g>

      {showMenu && !readonly && (
        <BendingPoint
          position={bendingPoint}
          onDragStart={handleBendingPointDragStart}
          onDrag={handleBendingPointDrag}
          onDragEnd={handleBendingPointDragEnd}
          onDblClick={handleBendingPointDblClick}
        />
      )}

      {showMenu && !readonly && (
        <AnnotationLinkMenu
          position={bendingPoint.addXY(0, -HitAreaWidth)}
          currentStyle={link.style}
          onStyleSelected={handleStyleSelected}
          onReverse={handleReverse}
          onDelete={handleDelete}
        />
      )}

      {!readonly && (
        <RightClickMenu
          isOpen={activeRightClickMenuIds.length === 1 && activeRightClickMenuIds[0] === id}
          mousePosition={bendingPoint}
        >
          <AnnotationLinkStyleMenuButton onStyleSelected={handleStyleSelected} />
          <RightClickMenuItem onClick={handleReverse} title={t('canvas.menu.reverseLink')} />
          <RightClickMenuItem onClick={handleDelete} title={t('common.action.delete')} />
        </RightClickMenu>
      )}
    </>
  );
};
